import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { XStack, YStack } from "tamagui";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminIconButton, AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppDialogSurface,
  AppEmptyState,
  AppFieldGroup,
  AppFormSection,
  AppInlineAlert,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  buildEventDescription,
  formatLocalityTags,
  isImportedEventDraft,
  isMemberSubmittedEventDraft,
  isTrustedReadyEventDraft,
  isTrustedWhatsOnSource,
  needsAdminEventAttention,
  normalizeEventRecord,
} from "../../shared/activities";

type EditorMode = "new" | "edit" | "duplicate" | "import" | null;
type QueueView = "attention" | "member" | "trusted" | "review" | "live";
type EventRow = Database["public"]["Tables"]["events"]["Row"];
type AdminEventRecord = ReturnType<typeof normalizeEventRecord>;

const emptyForm = {
  title: "",
  description: "",
  location: "",
  event_date: "",
  is_free: true,
  cost: "",
  url: "",
  image_url: "",
  source_name: "",
  locality_tags: "",
  is_published: false,
};

const toDateTimeLocalValue = (value: string) => {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const truncateReviewCopy = (value: string, maxLength = 180) =>
  value.length <= maxLength ? value : `${value.slice(0, maxLength - 1).trim()}…`;

const AdminEvents = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<AdminEventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [queueView, setQueueView] = useState<QueueView>("attention");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchEvents = async () => {
    const { data, error } = await supabase.from("events").select("*").order("event_date", { ascending: true });

    if (error) {
      toast.error("Could not load events");
      setLoading(false);
      return;
    }

    const nextEvents = ((data ?? []) as EventRow[])
      .map((event) => normalizeEventRecord(event))
      .sort((left, right) => {
        if (left.is_published !== right.is_published) {
          return left.is_published ? 1 : -1;
        }

        return new Date(left.event_date).getTime() - new Date(right.event_date).getTime();
      });

    setEvents(nextEvents);
    setLoading(false);
  };

  useEffect(() => {
    void fetchEvents();
  }, []);

  const editingEvent = useMemo(
    () => events.find((event) => event.id === editingId) ?? null,
    [editingId, events],
  );
  const reviewEvents = useMemo(() => events.filter((event) => !event.is_published), [events]);
  const liveEvents = useMemo(() => events.filter((event) => event.is_published), [events]);
  const memberSubmittedDrafts = useMemo(
    () => reviewEvents.filter((event) => isMemberSubmittedEventDraft(event)),
    [reviewEvents],
  );
  const importedDrafts = useMemo(() => reviewEvents.filter((event) => isImportedEventDraft(event)), [reviewEvents]);
  const trustedReadyDrafts = useMemo(
    () => reviewEvents.filter((event) => isTrustedReadyEventDraft(event)),
    [reviewEvents],
  );
  const attentionDrafts = useMemo(
    () => reviewEvents.filter((event) => needsAdminEventAttention(event)),
    [reviewEvents],
  );
  const visibleEvents = useMemo(() => {
    switch (queueView) {
      case "attention":
        return attentionDrafts;
      case "member":
        return memberSubmittedDrafts;
      case "trusted":
        return trustedReadyDrafts;
      case "review":
        return reviewEvents;
      case "live":
        return liveEvents;
      default:
        return reviewEvents;
    }
  }, [attentionDrafts, liveEvents, memberSubmittedDrafts, queueView, reviewEvents, trustedReadyDrafts]);

  const openNew = () => {
    setEditorMode("new");
    setEditingId(null);
    setForm(emptyForm);
  };

  const openImport = () => {
    setEditorMode("import");
    setEditingId(null);
    setForm({ ...emptyForm, is_published: false });
  };

  const populateFormFromEvent = (event: AdminEventRecord) => {
    setForm({
      title: event.title ?? "",
      description: event.description ?? "",
      location: event.location ?? "",
      event_date: event.event_date ? toDateTimeLocalValue(event.event_date) : "",
      is_free: event.is_free ?? true,
      cost: event.cost ?? "",
      url: event.url ?? "",
      image_url: event.image_url ?? "",
      source_name: event.source_name ?? "",
      locality_tags: formatLocalityTags(event.locality_tags),
      is_published: event.is_published ?? false,
    });
  };

  const openEdit = (event: AdminEventRecord) => {
    setEditorMode("edit");
    setEditingId(event.id);
    populateFormFromEvent(event);
  };

  const openDuplicate = (event: AdminEventRecord) => {
    setEditorMode("duplicate");
    setEditingId(event.id);
    populateFormFromEvent(event);
  };

  const closeDialog = () => {
    setEditorMode(null);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.location.trim() || !form.event_date) {
      toast.error("Add the title, description, location, and date before saving");
      return;
    }

    setSaving(true);

    const payload = {
      title: form.title.trim(),
      description: buildEventDescription(form.description, {
        sourceName: form.source_name,
        localityTags: form.locality_tags
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      }),
      location: form.location.trim(),
      event_date: new Date(form.event_date).toISOString(),
      is_free: form.is_free,
      cost: form.is_free ? null : form.cost.trim() || null,
      url: form.url.trim() || null,
      image_url: form.image_url.trim() || null,
      is_published: editorMode === "import" ? false : form.is_published,
      submitted_by: user?.id ?? null,
    };

    const result =
      editorMode === "edit" && editingId
        ? await supabase.from("events").update(payload).eq("id", editingId)
        : await supabase.from("events").insert(payload);

    setSaving(false);

    if (result.error) {
      toast.error(result.error.message || "Could not save event");
      return;
    }

    toast.success(
      editorMode === "import" ? "Draft imported for review" : editorMode === "duplicate" ? "Event duplicated" : "Event saved",
    );
    closeDialog();
    await fetchEvents();
  };

  const togglePublished = async (event: AdminEventRecord) => {
    const { error } = await supabase.from("events").update({ is_published: !event.is_published }).eq("id", event.id);

    if (error) {
      toast.error(error.message || "Could not update publishing status");
      return;
    }

    toast.success(event.is_published ? "Event unpublished" : "Event published");
    await fetchEvents();
  };

  const publishTrustedDrafts = async () => {
    if (!trustedReadyDrafts.length) return;

    const { error } = await supabase
      .from("events")
      .update({ is_published: true })
      .in("id", trustedReadyDrafts.map((event) => event.id));

    if (error) {
      toast.error(error.message || "Could not publish trusted drafts");
      return;
    }

    toast.success(`${trustedReadyDrafts.length} trusted drafts published`);
    await fetchEvents();
  };

  const rejectImportedDrafts = async () => {
    if (!importedDrafts.length) return;
    if (!window.confirm(`Reject ${importedDrafts.length} imported drafts? This cannot be undone.`)) return;

    const { error } = await supabase
      .from("events")
      .delete()
      .in("id", importedDrafts.map((event) => event.id));

    if (error) {
      toast.error(error.message || "Could not reject imported drafts");
      return;
    }

    toast.success(`${importedDrafts.length} imported drafts rejected`);
    await fetchEvents();
  };

  const deleteEvent = async (
    eventId: string,
    copy: { confirm: string; success: string },
  ) => {
    if (!window.confirm(copy.confirm)) return;

    const { error } = await supabase.from("events").delete().eq("id", eventId);
    if (error) {
      toast.error(error.message || "Could not delete event");
      return;
    }

    toast.success(copy.success);
    await fetchEvents();
  };

  return (
    <AdminPageScaffold
      title="Events"
      subtitle="Review member-submitted event links first, then work through imported drafts and ready-to-publish listings."
      fallbackPath="/admin"
      action={<AdminIconButton icon={Plus} label="Add event" onPress={openNew} />}
    >
      <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title="Review queue"
            subtitle="Use Member submissions for links shared by families. Needs review is for harder imported drafts. Ready to publish is the calmer lane for official-source imports that already have the basics in place."
            action={
              <XStack gap="$sm" flexWrap="wrap" justifyContent="flex-end">
                {queueView !== "live" && trustedReadyDrafts.length > 0 ? (
                  <AppButton type="button" tone="secondary" size="sm" onPress={() => void publishTrustedDrafts()}>
                    Publish ready imports ({trustedReadyDrafts.length})
                  </AppButton>
                ) : null}
                {queueView !== "live" && importedDrafts.length > 0 ? (
                  <AppButton type="button" tone="danger" size="sm" onPress={() => void rejectImportedDrafts()}>
                    Reject imported
                  </AppButton>
                ) : null}
                <AppButton type="button" tone="outline" size="sm" onPress={openImport}>
                  <Upload size={16} />
                  Import draft
                </AppButton>
              </XStack>
            }
          />
        </AppCardHeader>
        <AppCardBody>
          <YStack gap="$sm">
            <AppButtonRow justifyContent="flex-start">
              <AppButton
                type="button"
                tone={queueView === "attention" ? "secondary" : "outline"}
                size="sm"
                onPress={() => setQueueView("attention")}
              >
                Needs review ({attentionDrafts.length})
              </AppButton>
              <AppButton
                type="button"
                tone={queueView === "member" ? "secondary" : "outline"}
                size="sm"
                onPress={() => setQueueView("member")}
              >
                Member submissions ({memberSubmittedDrafts.length})
              </AppButton>
              <AppButton
                type="button"
                tone={queueView === "trusted" ? "secondary" : "outline"}
                size="sm"
                onPress={() => setQueueView("trusted")}
              >
                Ready to publish ({trustedReadyDrafts.length})
              </AppButton>
              <AppButton
                type="button"
                tone={queueView === "review" ? "secondary" : "outline"}
                size="sm"
                onPress={() => setQueueView("review")}
              >
                All drafts ({reviewEvents.length})
              </AppButton>
              <AppButton
                type="button"
                tone={queueView === "live" ? "secondary" : "outline"}
                size="sm"
                onPress={() => setQueueView("live")}
              >
                Live ({liveEvents.length})
              </AppButton>
            </AppButtonRow>
            <AppInlineAlert title="How to move quickly" tone="info">
              Stay in Member submissions for links shared by families. Use Needs review for imported drafts that still need a real judgement call. Use Ready to publish for official-source imports that already have source, place, image, and free or low-cost signals lined up.
            </AppInlineAlert>
          </YStack>
        </AppCardBody>
      </AppCard>

      {loading ? (
        <AppLoadingState
          title="Loading events"
          body="Fetching What's On listings and current publishing status."
        />
      ) : events.length === 0 ? (
        <AppEmptyState
          title="No events yet"
          body="Create the first listing or import a reviewed draft from an official local source."
          action={
            <AppButton type="button" onPress={openNew}>
              Add event
            </AppButton>
          }
        />
      ) : (
        <YStack gap="$sm">
          {visibleEvents.length === 0 ? (
            <AppEmptyState
              title={queueView === "live" ? "No live events" : "No drafts waiting"}
              body={
                queueView === "live"
                  ? "Publish reviewed listings to build the live What’s On feed."
                  : queueView === "trusted"
                    ? "No official-source imports are ready for one-tap publish right now."
                    : queueView === "member"
                      ? "No member-submitted event links are waiting right now."
                    : queueView === "attention"
                      ? "Nothing in the draft queue needs a manual judgement right now."
                      : "The importer has nothing new waiting right now."
              }
            />
          ) : null}
          {visibleEvents.map((event) => (
            <AppCard key={event.id}>
              <AppCardBody>
                <XStack alignItems="flex-start" gap="$md" flexWrap="wrap">
                  <div className="h-28 w-36 shrink-0 overflow-hidden rounded-[24px] border border-black/5 bg-black/5">
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-4 text-center text-xs font-medium text-muted-foreground">
                        No image
                      </div>
                    )}
                  </div>

                  <YStack flex={1} minWidth={240} gap={6}>
                    <XStack alignItems="center" gap="$sm" flexWrap="wrap">
                      <AppText fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                        {event.title}
                      </AppText>
                      <AdminStatusPill
                        tone={
                          event.is_published
                            ? "success"
                            : isTrustedReadyEventDraft(event)
                              ? "success"
                              : isMemberSubmittedEventDraft(event)
                                ? "primary"
                                : "warning"
                        }
                      >
                        {event.is_published
                          ? "Live"
                          : isTrustedReadyEventDraft(event)
                            ? "Ready to publish"
                            : isMemberSubmittedEventDraft(event)
                              ? "Member submission"
                              : "Needs review"}
                      </AdminStatusPill>
                      {!event.is_published && isMemberSubmittedEventDraft(event) ? (
                        <AdminStatusPill tone="secondary">Admin review before publish</AdminStatusPill>
                      ) : null}
                      {!event.is_published && isTrustedWhatsOnSource(event.source_name) ? (
                        <AdminStatusPill tone="primary">Official source</AdminStatusPill>
                      ) : null}
                      {event.relevance_score > 0 ? (
                        <AdminStatusPill tone="secondary">Local tagged</AdminStatusPill>
                      ) : null}
                      {!event.is_free && event.cost ? (
                        <AdminStatusPill tone="secondary">{event.cost}</AdminStatusPill>
                      ) : event.is_free ? (
                        <AdminStatusPill tone="secondary">Free</AdminStatusPill>
                      ) : null}
                      {event.image_url ? <AdminStatusPill tone="secondary">Image</AdminStatusPill> : null}
                    </XStack>

                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      {format(new Date(event.event_date), "EEE d MMM yyyy, h:mma")}
                      {event.location ? ` · ${event.location}` : ""}
                    </AppText>

                    <XStack gap="$sm" flexWrap="wrap">
                      <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                        {event.is_free ? "Free" : event.cost || "Paid event"}
                      </AppText>
                      {event.source_name ? (
                        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                          Source · {event.source_name}
                        </AppText>
                      ) : null}
                    </XStack>

                    {event.locality_tags?.length ? (
                      <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                        Local tags · {formatLocalityTags(event.locality_tags)}
                      </AppText>
                    ) : null}

                    {event.description ? (
                      <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                        {truncateReviewCopy(event.description)}
                      </AppText>
                    ) : null}

                    <AppButtonRow justifyContent="flex-start">
                      <AppButton
                        type="button"
                        tone={event.is_published ? "outline" : "secondary"}
                        size="sm"
                        onPress={() => void togglePublished(event)}
                      >
                        {event.is_published ? "Move to draft" : isMemberSubmittedEventDraft(event) ? "Review & publish" : "Publish"}
                      </AppButton>
                      {event.url ? (
                        <AppButton
                          type="button"
                          tone="outline"
                          size="sm"
                          onPress={() => window.open(event.url ?? "", "_blank", "noopener,noreferrer")}
                        >
                          <ExternalLink size={14} />
                          Open source
                        </AppButton>
                      ) : null}
                      <AppButton type="button" tone="outline" size="sm" onPress={() => openEdit(event)}>
                        Edit
                      </AppButton>
                      <AppButton type="button" tone="outline" size="sm" onPress={() => openDuplicate(event)}>
                        Duplicate
                      </AppButton>
                      <AppButton
                        type="button"
                        tone="danger"
                        size="sm"
                        onPress={() =>
                          void deleteEvent(event.id, {
                            confirm: event.is_published
                              ? "Delete this live event? This cannot be undone."
                              : isMemberSubmittedEventDraft(event)
                                ? "Reject this member submission? This cannot be undone."
                                : "Reject this draft? This cannot be undone.",
                            success: event.is_published
                              ? "Event deleted"
                              : isMemberSubmittedEventDraft(event)
                                ? "Member submission rejected"
                                : "Draft rejected",
                          })
                        }
                      >
                        <Trash2 size={14} />
                        {event.is_published ? "Delete" : isMemberSubmittedEventDraft(event) ? "Reject submission" : "Reject"}
                      </AppButton>
                    </AppButtonRow>
                  </YStack>
                </XStack>
              </AppCardBody>
            </AppCard>
          ))}
        </YStack>
      )}

      <AppDialogSurface
        open={editorMode !== null}
        onOpenChange={(open) => !open && closeDialog()}
        title={
          editorMode === "import"
            ? "Import event draft"
            : editorMode === "edit"
              ? `Edit ${editingEvent?.title ?? "event"}`
              : editorMode === "duplicate"
                ? `Duplicate ${editingEvent?.title ?? "event"}`
                : "Add event"
        }
        description={
          editorMode === "import"
            ? "Bring in a vetted external event as an unpublished draft, then review and publish it."
            : editorMode === "duplicate"
              ? "Copy the event details, change the date or anything else needed, then save it as a new listing."
              : "Manage What's On listings and publishing status."
        }
        maxWidthClassName="max-w-3xl"
      >
        <div className="max-h-[70vh] overflow-y-auto pr-1">

          <YStack gap="$md" mt="$md">
            <AppFormSection>
              <AppFieldGroup label="Title">
                <AppInput value={form.title} onChangeText={(value) => setForm((current) => ({ ...current, title: value }))} />
              </AppFieldGroup>
              <AppFieldGroup label="Description">
                <AppTextArea
                  value={form.description}
                  onChangeText={(value) => setForm((current) => ({ ...current, description: value }))}
                  rows={5}
                />
              </AppFieldGroup>
              <AppFieldGroup label="Location">
                <AppInput value={form.location} onChangeText={(value) => setForm((current) => ({ ...current, location: value }))} />
              </AppFieldGroup>
              <AppFieldGroup label="Date & time">
                <AppInput
                  type="datetime-local"
                  value={form.event_date}
                  onChangeText={(value) => setForm((current) => ({ ...current, event_date: value }))}
                />
              </AppFieldGroup>
              <AppFieldGroup label="Source URL">
                <AppInput
                  value={form.url}
                  onChangeText={(value) => setForm((current) => ({ ...current, url: value }))}
                  placeholder="https://..."
                />
              </AppFieldGroup>
              <AppFieldGroup label="Source name">
                <AppInput
                  value={form.source_name}
                  onChangeText={(value) => setForm((current) => ({ ...current, source_name: value }))}
                  placeholder="Cheshire West and Chester Libraries"
                />
              </AppFieldGroup>
              <AppFieldGroup label="Image URL">
                <AppInput
                  value={form.image_url}
                  onChangeText={(value) => setForm((current) => ({ ...current, image_url: value }))}
                  placeholder="https://..."
                />
              </AppFieldGroup>
              <AppFieldGroup
                label="Local area tags"
                helper="Comma-separated outward postcodes and area names so the feed can rank this locally."
              >
                <AppInput
                  value={form.locality_tags}
                  onChangeText={(value) => setForm((current) => ({ ...current, locality_tags: value }))}
                  placeholder="cw8, northwich, cheshire"
                />
              </AppFieldGroup>
              <AppFieldGroup label="Price type">
                <AppButtonRow>
                  <AppButton
                    type="button"
                    tone={form.is_free ? "secondary" : "outline"}
                    size="sm"
                    onPress={() => setForm((current) => ({ ...current, is_free: true, cost: "" }))}
                  >
                    Free event
                  </AppButton>
                  <AppButton
                    type="button"
                    tone={!form.is_free ? "secondary" : "outline"}
                    size="sm"
                    onPress={() => setForm((current) => ({ ...current, is_free: false }))}
                  >
                    Paid event
                  </AppButton>
                </AppButtonRow>
              </AppFieldGroup>
              {!form.is_free ? (
                <AppFieldGroup label="Cost">
                  <AppInput
                    value={form.cost}
                    onChangeText={(value) => setForm((current) => ({ ...current, cost: value }))}
                    placeholder="e.g. £8 per child"
                  />
                </AppFieldGroup>
              ) : null}
              {editorMode !== "import" ? (
                <AppFieldGroup
                  label="Publishing"
                  helper={editorMode === "duplicate" ? "The duplicate keeps the current publishing state unless you change it before saving." : undefined}
                >
                  <AppButtonRow>
                    <AppButton
                      type="button"
                      tone={form.is_published ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setForm((current) => ({ ...current, is_published: true }))}
                    >
                      Published
                    </AppButton>
                    <AppButton
                      type="button"
                      tone={!form.is_published ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setForm((current) => ({ ...current, is_published: false }))}
                    >
                      Draft
                    </AppButton>
                  </AppButtonRow>
                </AppFieldGroup>
              ) : null}
              <AppButtonRow justifyContent="flex-end">
                <AppButton type="button" tone="outline" onPress={closeDialog}>
                  Cancel
                </AppButton>
                {editorMode === "edit" && editingEvent ? (
                  <AppButton type="button" tone="outline" onPress={() => openDuplicate(editingEvent)} disabled={saving}>
                    Duplicate event
                  </AppButton>
                ) : null}
                <AppButton type="button" onPress={() => void save()} disabled={saving}>
                  {saving
                    ? "Saving..."
                    : editorMode === "import"
                      ? "Import draft"
                      : editorMode === "duplicate"
                        ? "Create duplicate"
                        : "Save event"}
                </AppButton>
              </AppButtonRow>
            </AppFormSection>
          </YStack>
        </div>
      </AppDialogSurface>
    </AdminPageScaffold>
  );
};

export default AdminEvents;
